/**
 * Suppliers, purchase orders, deliveries, and the three-way match.
 *
 * Three things the types are deliberate about.
 *
 * A purchase order keeps `quantity` and `received_quantity` side by side, and
 * `outstanding` is the server's arithmetic on the pair. A screen must never
 * show one as if it were the other: "we ordered ten and they sent eight" is the
 * fact the whole module exists to preserve.
 *
 * `estimated_cost` and `supplier_name` on a suggestion are nullable, and null
 * means the item is on nobody's price list — not that it is free and not that
 * the supplier is unknown-and-therefore-any. Same shape as the withheld dash.
 *
 * A match returns per-line findings and a total, in that order. The total is
 * never the headline: two errors that cancel sum to zero, and finding exactly
 * that is what the control is for.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface Supplier {
  id: string;
  code: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  /** Net days. Null means nobody recorded them, not cash on delivery. */
  payment_terms_days: number | null;
  is_active: boolean;
}

export interface SupplierPrice {
  id: string;
  supplier_id: string;
  stock_item_id: string;
  stock_item_name: string;
  /** What the supplier calls it — rarely what the kitchen calls it. */
  supplier_sku: string | null;
  pack_size: string;
  /** What they charge today, with every agreed change that has started. */
  pack_price: string;
  /** What it was listed at, before any of them. */
  listed_price: string;
  /** The next agreed rise that has not started yet. */
  next_price: string | null;
  next_price_from: string | null;
  /** Proposals waiting on somebody. */
  awaiting_decision: number;
  minimum_order: string;
  lead_time_days: number | null;
  is_preferred: boolean;
}

export type PurchaseOrderState = 'draft' | 'approved' | 'receiving' | 'closed' | 'cancelled';

export interface PurchaseOrderLine {
  id: string;
  stock_item_id: string;
  name: string;
  unit: string;
  quantity: string;
  /** Frozen when the line was written. A later price rise is a finding. */
  unit_price: string;
  received_quantity: string;
  /**
   * Sent back to the supplier since. Kept beside `received_quantity`, never
   * subtracted from it: what arrived is a fact about the delivery, and the
   * three-way match reads it. What can still go back is received less this.
   */
  returned_quantity: string;
  outstanding: string;
  line_total: string;
  note: string | null;
}

export interface PurchaseOrder {
  id: string;
  number: number;
  supplier_id: string;
  supplier_name: string;
  location_id: string;
  storage_id: string | null;
  state: PurchaseOrderState;
  expected_on: string | null;
  note: string | null;
  total: string;
  approved_at: string | null;
  lines: PurchaseOrderLine[];
}

export interface ReorderSuggestion {
  stock_item_id: string;
  name: string;
  /** Null when the item is on nobody's price list. */
  supplier_id: string | null;
  supplier_name: string | null;
  shortfall: string;
  packs: number;
  quantity: string;
  /** How far the pack rounding overshoots the gap. */
  overshoot: string;
  estimated_cost: string | null;
  /** The supplier will not sell less than this, so the line is more than needed. */
  below_minimum: boolean;
}

export type MatchFinding =
  | 'matched'
  | 'within_tolerance'
  | 'short_delivery'
  | 'over_delivery'
  | 'price_increase'
  | 'price_decrease'
  | 'unordered'
  | 'unbilled'
  | 'overbilled'
  | 'underbilled';

export interface MatchLine {
  stock_item_id: string;
  name: string;
  findings: MatchFinding[];
  quantity_variance: string;
  price_variance: string;
  value_variance: string;
}

export interface InvoiceMatch {
  invoice_id: string;
  reference: string;
  state: string;
  is_clean: boolean;
  total_variance: string;
  lines: MatchLine[];
}

/** In the words a buyer would use, not the enum. */
export const FINDING_LABELS: Record<MatchFinding, string> = {
  matched: 'Agrees',
  within_tolerance: 'Within tolerance',
  short_delivery: 'Short delivery',
  over_delivery: 'More than ordered',
  price_increase: 'Price went up',
  price_decrease: 'Price went down',
  unordered: 'Not ordered',
  unbilled: 'Delivered, not billed',
  overbilled: 'Billed for more than arrived',
  underbilled: 'Billed for less than arrived',
};

export const PURCHASE_ORDER_STATE_LABELS: Record<PurchaseOrderState, string> = {
  draft: 'Draft',
  approved: 'Placed',
  receiving: 'Part delivered',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
  // A delivery moves stock, so the inventory screens are stale too.
  void queryClient.invalidateQueries({ queryKey: ['inventory'] });
}

export function useSuppliers(includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'suppliers', includeInactive],
    queryFn: ({ signal }) =>
      api.get<Supplier[]>(
        `/v1/purchasing/suppliers?include_inactive=${includeInactive}`,
        signal,
      ),
    staleTime: 5 * 60_000,
  });
}

export function useAddSupplier() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Supplier,
    ApiError,
    {
      code: string;
      name: string;
      contact_name?: string;
      email?: string;
      phone?: string;
      payment_terms_days?: number;
    }
  >({
    mutationFn: (input) => api.post<Supplier>('/v1/purchasing/suppliers', input),
    onSettled: () => invalidate(queryClient),
  });
}

export function useSupplierPrices(supplierId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'prices', supplierId ?? null],
    queryFn: ({ signal }) =>
      api.get<SupplierPrice[]>(`/v1/purchasing/suppliers/${supplierId}/prices`, signal),
    enabled: Boolean(supplierId),
    staleTime: 5 * 60_000,
  });
}

export function useSetSupplierPrice() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    SupplierPrice,
    ApiError,
    {
      supplierId: string;
      stockItemId: string;
      packSize: string;
      packPrice: string;
      supplierSku?: string;
      minimumOrder?: string;
      leadTimeDays?: number;
      isPreferred?: boolean;
    }
  >({
    mutationFn: (input) =>
      api.request<SupplierPrice>(`/v1/purchasing/suppliers/${input.supplierId}/prices`, {
        method: 'PUT',
        body: {
          stock_item_id: input.stockItemId,
          pack_size: input.packSize,
          pack_price: input.packPrice,
          supplier_sku: input.supplierSku ?? null,
          minimum_order: input.minimumOrder ?? '0',
          lead_time_days: input.leadTimeDays ?? null,
          is_preferred: input.isPreferred ?? false,
        },
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export function usePurchaseOrders(locationId: string | undefined, state?: PurchaseOrderState) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'orders', locationId ?? null, state ?? null],
    queryFn: ({ signal }) =>
      api.get<PurchaseOrder[]>(
        `/v1/purchasing/orders?location_id=${locationId}${state ? `&state=${state}` : ''}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 30_000,
  });
}

export function usePurchaseOrder(orderId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'order', orderId ?? null],
    queryFn: ({ signal }) => api.get<PurchaseOrder>(`/v1/purchasing/orders/${orderId}`, signal),
    enabled: Boolean(orderId),
    staleTime: 0,
  });
}

export function useCreatePurchaseOrder() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PurchaseOrder,
    ApiError,
    {
      supplierId: string;
      locationId: string;
      storageId?: string;
      expectedOn?: string;
      note?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<PurchaseOrder>('/v1/purchasing/orders', {
        supplier_id: input.supplierId,
        location_id: input.locationId,
        storage_id: input.storageId ?? null,
        expected_on: input.expectedOn ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Put a line on a draft order.
 *
 * Omit `unitPrice` to take the supplier's list price, which is then frozen. If
 * the item is on nobody's list the server refuses and says so, rather than
 * inventing a price.
 */
export function useAddOrderLine() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PurchaseOrder,
    ApiError,
    {
      orderId: string;
      stockItemId: string;
      quantity: string;
      unitPrice?: string;
      note?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<PurchaseOrder>(`/v1/purchasing/orders/${input.orderId}/lines`, {
        stock_item_id: input.stockItemId,
        quantity: input.quantity,
        unit_price: input.unitPrice ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Commit the business to the supplier's money. Separate permission from drafting. */
export function useApproveOrder() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PurchaseOrder, ApiError, { orderId: string }>({
    mutationFn: ({ orderId }) =>
      api.post<PurchaseOrder>(`/v1/purchasing/orders/${orderId}/approve`),
    onSettled: () => invalidate(queryClient),
  });
}

export function useCancelPurchaseOrder() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PurchaseOrder, ApiError, { orderId: string }>({
    mutationFn: ({ orderId }) =>
      api.post<PurchaseOrder>(`/v1/purchasing/orders/${orderId}/cancel`),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Book a delivery in. `lines` is {stock_item_id: quantity} in stock units.
 *
 * Part deliveries are ordinary — half on Tuesday, half on Thursday — and leave
 * the order in `receiving` with the balance outstanding.
 */
export function useReceiveDelivery() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PurchaseOrder,
    ApiError,
    {
      orderId: string;
      lines: Record<string, string>;
      storageId?: string;
      deliveryNote?: string;
      note?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<PurchaseOrder>(`/v1/purchasing/orders/${input.orderId}/receive`, {
        lines: input.lines,
        storage_id: input.storageId ?? null,
        delivery_note: input.deliveryNote ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Give up on the rest. The outstanding quantity stays on the order. */
export function useCloseOrderShort() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PurchaseOrder, ApiError, { orderId: string }>({
    mutationFn: ({ orderId }) =>
      api.post<PurchaseOrder>(`/v1/purchasing/orders/${orderId}/close`),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * What to order, biggest shortfall first.
 *
 * Never cached long: the buyer is deciding what to spend, and a stale answer
 * either double-orders or misses a gap.
 */
export function useReorderSuggestions(
  locationId: string | undefined,
  storageId: string | undefined,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'suggestions', locationId ?? null, storageId ?? null],
    queryFn: ({ signal }) =>
      api.get<ReorderSuggestion[]>(
        `/v1/purchasing/suggestions?location_id=${locationId}&storage_id=${storageId}`,
        signal,
      ),
    enabled: Boolean(locationId && storageId),
    staleTime: 0,
  });
}

export function useRecordInvoice() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { id: string },
    ApiError,
    {
      supplierId: string;
      reference: string;
      invoiceDate: string;
      total: string;
      lines: { stock_item_id: string; quantity: string; unit_price: string }[];
      purchaseOrderId?: string;
      dueOn?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<{ id: string }>('/v1/purchasing/invoices', {
        supplier_id: input.supplierId,
        reference: input.reference,
        invoice_date: input.invoiceDate,
        total: input.total,
        lines: input.lines,
        purchase_order_id: input.purchaseOrderId ?? null,
        due_on: input.dueOn ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Reconcile the order, the delivery and the invoice.
 *
 * Tolerances take an absolute floor as well as a percentage: 2% of a £2 item
 * is fourpence and not worth chasing, while 2% of a £900 meat order is £18 and
 * is. Both apply, and the larger wins.
 */
export function useMatchInvoice() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    InvoiceMatch,
    ApiError,
    {
      invoiceId: string;
      quantityPercent?: string;
      quantityAbsolute?: string;
      pricePercent?: string;
      priceAbsolute?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<InvoiceMatch>(`/v1/purchasing/invoices/${input.invoiceId}/match`, {
        quantity_percent: input.quantityPercent ?? '0',
        quantity_absolute: input.quantityAbsolute ?? '0',
        price_percent: input.pricePercent ?? '2',
        price_absolute: input.priceAbsolute ?? '0.02',
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Whether a finding needs somebody to do something about it. */
export function isException(finding: MatchFinding): boolean {
  return finding !== 'matched' && finding !== 'within_tolerance';
}

/** What the supplier agreed they owe back. */
export interface CreditNote {
  id: string;
  supplier_id: string;
  supplier_name: string;
  supplier_invoice_id: string | null;
  invoice_reference: string | null;
  reference: string;
  credit_date: string;
  total: string;
  reason: string;
  /**
   * Set on a return: the order its goods came in on and the storage they went
   * back out of. Null on a credit that moved no stock.
   */
  purchase_order_id: string | null;
  storage_id: string | null;
}

/** What is actually owed on an invoice once its credits are counted. */
export interface Payable {
  supplier_invoice_id: string;
  reference: string;
  invoiced: string;
  credited: string;
  payable: string;
}

/** What is still owed on one invoice, once every credit against it is counted. */
export function usePayable(invoiceId: string | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'payable', invoiceId ?? null],
    queryFn: ({ signal }) =>
      api.get<Payable>(`/v1/purchasing/invoices/${invoiceId}/payable`, signal),
    enabled: Boolean(invoiceId),
    staleTime: 30_000,
  });
}

export function useCreditNotes({ enabled = true }: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'credit-notes'],
    queryFn: ({ signal }) => api.get<CreditNote[]>('/v1/purchasing/credit-notes', signal),
    // Professional and above, and the caller says whether the plan has it —
    // a 402 on a page load helps nobody.
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Record what the supplier agreed they owe back.
 *
 * `supplier_invoice_id` is optional because credits arrive that are not against
 * one bill: a quarterly rebate, a goodwill credit after a bad week.
 */
export function useRecordCreditNote() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { id: string },
    ApiError,
    {
      supplierId: string;
      reference: string;
      creditDate: string;
      total: string;
      reason: string;
      supplierInvoiceId?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<{ id: string }>('/v1/purchasing/credit-notes', {
        supplier_id: input.supplierId,
        reference: input.reference,
        credit_date: input.creditDate,
        total: input.total,
        reason: input.reason,
        ...(input.supplierInvoiceId ? { supplier_invoice_id: input.supplierInvoiceId } : {}),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
    },
  });
}

/** The returns made against one order, newest first. */
export function useOrderReturns(orderId: string | undefined, { enabled = true } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'credit-notes', 'order', orderId ?? null],
    queryFn: ({ signal }) =>
      api.get<CreditNote[]>(`/v1/purchasing/credit-notes?purchase_order_id=${orderId}`, signal),
    enabled: enabled && Boolean(orderId),
    staleTime: 30_000,
  });
}

export interface RecordReturnInput {
  orderId: string;
  /** {stock_item_id: quantity}, in each item's stock unit, as the order's are. */
  lines: Record<string, string>;
  /** The supplier's reference for the credit they are giving. */
  reference: string;
  creditDate: string;
  reason: string;
  /** Where the goods go back out of. Omitted means the order's own storage. */
  storageId?: string;
  supplierInvoiceId?: string;
}

/**
 * Send goods from an order back to the supplier: stock out, credit in, as one
 * act.
 *
 * Wrong or excess goods used to leave only as waste or a count correction, and
 * both read as loss on the variance report. A return is priced at what the order
 * agreed, moves the stock and records the credit together, and stays out of the
 * variance report the way a transfer does. Only what arrived can go back — the
 * server refuses more, and says how much can.
 */
export function useRecordReturn() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { credit_note_id: string; total: string; order: PurchaseOrder },
    ApiError,
    RecordReturnInput
  >({
    mutationFn: (input) =>
      api.post(`/v1/purchasing/orders/${input.orderId}/returns`, {
        lines: Object.entries(input.lines).map(([stockItemId, quantity]) => ({
          stock_item_id: stockItemId,
          quantity,
        })),
        reference: input.reference,
        credit_date: input.creditDate,
        reason: input.reason,
        storage_id: input.storageId ?? null,
        ...(input.supplierInvoiceId ? { supplier_invoice_id: input.supplierInvoiceId } : {}),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
      // The goods left a shelf.
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/** A price the supplier wants, from a date, once somebody agrees to it. */
export interface PriceChange {
  id: string;
  supplier_item_id: string;
  stock_item_name: string;
  from_price: string;
  to_price: string;
  effective_from: string;
  state: 'proposed' | 'approved' | 'rejected';
  note: string | null;
  decided_at: string | null;
}

export function usePriceChanges(supplierId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['purchasing', 'price-changes', supplierId],
    queryFn: ({ signal }) =>
      api.get<PriceChange[]>(`/v1/purchasing/suppliers/${supplierId}/price-changes`, signal),
    enabled: enabled && Boolean(supplierId),
    staleTime: 30_000,
  });
}

/**
 * Record a price the supplier wants. Nothing changes until it is approved —
 * an increase typed straight onto the list used to be the price.
 */
export function useProposePrice() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { id: string },
    ApiError,
    { supplierItemId: string; toPrice: string; effectiveFrom: string; note?: string }
  >({
    mutationFn: (input) =>
      api.post<{ id: string }>(
        `/v1/purchasing/supplier-items/${input.supplierItemId}/price-changes`,
        {
          to_price: input.toPrice,
          effective_from: input.effectiveFrom,
          ...(input.note ? { note: input.note } : {}),
        },
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
    },
  });
}

export function useDecidePrice() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PriceChange, ApiError, { priceChangeId: string; approve: boolean }>({
    mutationFn: ({ priceChangeId, approve }) =>
      api.post<PriceChange>(
        `/v1/purchasing/price-changes/${priceChangeId}/${approve ? 'approve' : 'reject'}`,
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
    },
  });
}
